import { DeviceVariantsEnum } from '@ubnt/link-core/enums';
import PanelCard from '@ubnt/ui-components/PanelCard/PanelCard';
import { omit } from 'lodash-es';
import { memo, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import {
  isSupportedCustomClientWifiStandard,
  resolveConfigsShape,
  type CustomClientFormType,
} from '@ubnt/wifi-calculator/device/customClients';
import { isEnumValue } from '@ubnt/wifi-calculator/utils/isEnumValue';

import { Flex } from 'components/Flex';

import ChainCountDropdown from './ChainCountDropdown';
import DisableRadioCheckbox from './DisableRadioCheckbox';
import MaxChannelWidthPicker from './MaxChannelWidthPicker';

const RadioConfigsImpl = () => {
  const wifiStandard = useWatch<CustomClientFormType>({ name: 'wifiStandard' });
  const radioParams = useWatch<CustomClientFormType>({ name: 'radioParams' });

  const { control, setValue } = useFormContext();

  const configsShape = useMemo(() => {
    if (isSupportedCustomClientWifiStandard(wifiStandard)) {
      return resolveConfigsShape(wifiStandard);
    }
    return {};
  }, [wifiStandard]);

  const previousConfigsShape = usePrevious(configsShape);

  useEffect(() => {
    if (previousConfigsShape !== configsShape || !radioParams) {
      const newRadioParams: CustomClientFormType['radioParams'] = {};

      Object.entries(configsShape).forEach(([variant, shapeEntry]) => {
        const numberVariant = Number(variant);
        if (
          isEnumValue(DeviceVariantsEnum, numberVariant) &&
          (radioParams == null || typeof radioParams === 'object')
        ) {
          const previousRadioParams = radioParams?.[numberVariant];
          const maxChannelWidthOption = shapeEntry.channelWidthOptions[shapeEntry.channelWidthOptions.length - 1];
          newRadioParams[numberVariant] = {
            disabled: !!previousRadioParams?.disabled && shapeEntry.isDisablable,
            maxChannelWidth:
              (
                !previousRadioParams ||
                !shapeEntry.channelWidthOptions.find(
                  (option) => Number(option.value) === previousRadioParams?.maxChannelWidth
                )
              ) ?
                Number(maxChannelWidthOption.value)
              : previousRadioParams?.maxChannelWidth,
            chainCount: previousRadioParams?.chainCount ?? 2,
          };
        }
      });
      setValue('radioParams', newRadioParams);
    }
  }, [radioParams, previousConfigsShape, configsShape, setValue]);

  if (typeof radioParams !== 'object') {
    return null;
  }

  return [DeviceVariantsEnum.Alternative, DeviceVariantsEnum.Default, DeviceVariantsEnum.BetterAlternative].map(
    (variant) => {
      const value = configsShape[variant];
      if (!value) {
        return null;
      }
      return (
        <PanelCard header={value.label} isCollapsible={false} key={variant}>
          <Flex gap="l" column>
            <Flex justifyBetween>
              <Controller
                control={control}
                name={`radioParams.${variant}.maxChannelWidth`}
                render={({ field }) => (
                  <MaxChannelWidthPicker
                    disabled={radioParams[variant]?.disabled}
                    options={value.channelWidthOptions}
                    {...omit(field, 'ref')}
                  />
                )}
              />
              <Controller
                control={control}
                name={`radioParams.${variant}.chainCount`}
                render={({ field }) => (
                  <ChainCountDropdown
                    disabled={radioParams[variant]?.disabled}
                    maxChainCount={4}
                    {...omit(field, 'ref')}
                  />
                )}
              />
            </Flex>
            {value.isDisablable && (
              <Controller
                control={control}
                name={`radioParams.${variant}.disabled`}
                render={({ field }) => <DisableRadioCheckbox id={`disable-${variant}`} {...omit(field, 'ref')} />}
              />
            )}
          </Flex>
        </PanelCard>
      );
    }
  );
};

const RadioConfigs = memo(RadioConfigsImpl);

export default RadioConfigs;
